import { useEffect, useMemo, useState } from 'react';
import { usePermission } from '../../../auth/AuthContext';
import type { SelectOption } from '../../../components/inputs/Select';
import { personnelApi, personsApi } from './personnelApi';

/**
 * The employees a form can name — an evidence agent or custodian, a Cash Division requester.
 *
 * <b>It lives with Personnel</b> because that is its subject and two suites read it; it was
 * written for Evidence Management and moved here when Cash Division became its second consumer.
 *
 * <b>The shape the issuance form already takes</b>, and for its reason: a row carries a personnel
 * id and no name, so the picker lists the personnel page and resolves the names in ONE batch
 * through the identity service. A record above the reader's access level arrives with no
 * `personId` — that absence is the mask — so it is offered by employee number only.
 *
 * Fetched only while the form is OPEN, and only for a reader holding `personnel.read` (implied,
 * scoped to the writes that open these pickers). A failed load names no field, so it must not
 * reach the save path's error state.
 */
export function useStaffOptions(open: boolean): { options: SelectOption[]; loading: boolean } {
  const canRead = usePermission('personnel.read');
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !canRead) return;

    const controller = new AbortController();
    setLoading(true);

    personnelApi
      .list({ pageSize: 100 }, controller.signal)
      .then(async (page) => {
        const names = await personsApi.resolve(
          page.items.map((p) => p.personId).filter((id): id is string => id != null),
          controller.signal,
        );

        if (controller.signal.aborted) return;

        const byPerson = new Map(names.map((n) => [n.personId, n.displayName]));

        setOptions(
          page.items.map((p) => ({
            value: p.id,
            label: (p.personId != null ? byPerson.get(p.personId) : undefined) ?? p.employeeNumber,
            sublabel: p.orgUnitName ?? undefined,
            keywords: p.employeeNumber,
          })),
        );
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, canRead]);

  return { options, loading };
}

/**
 * Personnel id → what this page may print for it.
 *
 * One `get` per DISTINCT id, then ONE resolve — the issuance profile's hop. The ids on a page of
 * evidence are a handful of agents and custodians, not one per row. An erased or unresolvable
 * identity falls back to the employee number: the record still names somebody, this page cannot
 * print who.
 *
 * <b>`canRead` is returned with the names</b> so a caller renders `Hidden` for a reader without
 * `personnel.read` — never an em dash, which would state the item has no custodian.
 */
export function usePersonnelNames(ids: (string | null | undefined)[]): {
  names: Record<string, string>;
  canRead: boolean;
} {
  const canRead = usePermission('personnel.read');
  const [names, setNames] = useState<Record<string, string>>({});

  // A stable key over the DISTINCT ids, so a re-render with the same page fetches nothing.
  const key = useMemo(
    () => [...new Set(ids.filter((id): id is string => id != null))].sort().join(','),
    [ids],
  );

  useEffect(() => {
    if (!canRead || key === '') return;

    const controller = new AbortController();

    void (async () => {
      try {
        // SETTLED, not all: one employee above this reader's access level is a 403, and it
        // must cost that one name rather than every name on the page.
        const settled = await Promise.allSettled(
          key.split(',').map((personnelId) => personnelApi.get(personnelId, controller.signal)),
        );

        const records = settled.flatMap((s) => (s.status === 'fulfilled' ? [s.value] : []));

        const resolved = await personsApi.resolve(
          records.map((r) => r.personId).filter((id): id is string => id != null),
          controller.signal,
        );

        if (controller.signal.aborted) return;

        const byPerson = new Map(resolved.map((p) => [p.personId, p.displayName]));

        setNames(
          Object.fromEntries(
            records.map((r) => [r.id, (r.personId != null ? byPerson.get(r.personId) : undefined) ?? r.employeeNumber]),
          ),
        );
      } catch {
        // Left as it was — an unresolved name is stated by the cell, not by an error state.
      }
    })();

    return () => controller.abort();
  }, [key, canRead]);

  return { names, canRead };
}

/** What a personnel id reads as: null for none, `Hidden` without the key, else the name. */
export function personnelLabel(
  id: string | null | undefined,
  names: Record<string, string>,
  canRead: boolean,
): string | null {
  if (id == null) return null;
  if (!canRead) return 'Hidden';
  return names[id] ?? null;
}

/**
 * One PERSON id (not a personnel id) → its display name — the judge on a court transfer. One resolve,
 * under `personnel.read` like every name on these pages; a reader without it gets `Hidden`, and an
 * id the resolve answers `restricted` prints that word rather than nothing.
 */
export function usePersonName(personId: string | null | undefined): string | null {
  const canRead = usePermission('personnel.read');
  const [name, setName] = useState<{ id: string; value: string } | null>(null);

  useEffect(() => {
    if (!canRead || personId == null) return;

    const controller = new AbortController();

    personsApi
      .resolve([personId], controller.signal)
      .then((resolved) => {
        const one = resolved.find((p) => p.personId === personId);
        if (!controller.signal.aborted && one) setName({ id: personId, value: one.displayName });
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [personId, canRead]);

  if (personId == null) return null;
  if (!canRead) return 'Hidden';
  // Derived against the id, so a different id never shows the previous one's name.
  return name?.id === personId ? name.value : null;
}
